import React, { useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import { useDispatch, useSelector } from "react-redux";
import { togglePanelSelection } from "../../redux/pvDesign";
import { SimilarPolygonObjectsMesh } from "./InstancedMesh";
import { arePolygonsSimilar } from "../utility/dataManipulation";

const PVDesignViewer = () => {
  const dispatch = useDispatch();
  const {
    selectedRequestId,
    resultsByRequestId,
    activeDesignIndexByRequestId,
    selectedPanelIds,
  } = useSelector((s) => s.pvDesign);
  const designs = resultsByRequestId[selectedRequestId] || [];
  const activeIdx = Math.max(
    0,
    Math.min(
      activeDesignIndexByRequestId[selectedRequestId] || 0,
      Math.max(0, designs.length - 1)
    )
  );
  const active = designs[activeIdx];

  const groups = useMemo(() => {
    const res = [];
    const panels = active?.panels || [];
    for (const p of panels) {
      const polygon = p.polygon || [];
      let placed = false;
      for (const g of res) {
        if (
          arePolygonsSimilar(
            new Float32Array(g[0].polygon),
            new Float32Array(polygon)
          )
        ) {
          g.push({ ...p, polygon });
          placed = true;
          break;
        }
      }
      if (!placed) res.push([{ ...p, polygon }]);
    }
    return res;
  }, [active]);

  const values = useMemo(
    () => (active?.panels || []).map((p) => Number(p.annual_yield || 0)),
    [active]
  );
  const min = useMemo(
    () => (values.length ? Math.min(...values) : 0),
    [values]
  );
  const max = useMemo(
    () => (values.length ? Math.max(...values) : 1),
    [values]
  );

  const setColor = (obj) => {
    const isSelected = !!selectedPanelIds?.[obj.id];
    if (isSelected) return new THREE.Color(1, 1, 0);
    return new THREE.Color(0, 7 / 255, 71 / 255);
  };

  if (!selectedRequestId || !active) return null;

  return (
    <>
      {groups.map((PVs, i) => (
        <SimilarPolygonObjectsMesh
          key={`${selectedRequestId}-${activeIdx}-${i}-${selectedPanelIds}`}
          objects={PVs}
          setColor={setColor}
          updateDependencyArray={[
            min,
            max,
            PVs,
            selectedRequestId,
            activeIdx,
            selectedPanelIds,
          ]}
          groupProps={{ visible: true }}
          meshProps={{
            onClick: (e) => {
              e.stopPropagation();
              const instanceId = e.instanceId;
              if (typeof instanceId === "number") {
                const hit = PVs[instanceId];
                if (hit?.id) dispatch(togglePanelSelection(hit.id));
              }
            },
          }}
        />
      ))}
    </>
  );
};

export default PVDesignViewer;
